/**
 * HSECard — single Health/Safety/Environment card for the Performance tab.
 *
 * Renders one of five states based on the engine score's HSE extension:
 *   1. Clean             count = 0
 *   2. Fresh incident    count > 0 AND fresh_incident = true (within 48h)
 *   3. Settled           count > 0 AND fresh_incident = false (after 48h)
 *   4. Multi-incident    count > 1 (foot text shifts to "most recent: ...")
 *   5. Expanded          parent renders IncidentDrillDown panel below
 *
 * Layout invariant: card always preserves its 1.4:1 aspect ratio. Expansion
 * happens BELOW the row of three cards (rendered by the parent HSERow), not
 * by inflating this card. That preserves the "three squares across the top"
 * visual identity of the HSE row.
 *
 * Click target = entire card body except the "+ Report" button (which has
 * its own click handler with event.stopPropagation). When the card has no
 * incidents in the period, the click target is disabled — there's nothing
 * to drill into.
 *
 * Spec: docs/features/Life4Earth_HSE_Feature_Spec.md § 7.2
 * Mockup: mockups/hse_card_states.html
 * Epic: DEV-822 (HSE), Task: DEV-827
 */

import React, { useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type {
  HseCategory,
  HseScoreExtension,
  HseSeverity,
  SubKpiScore,
} from '../../kpi-engine/missionTypes/types';
import styles from './HSECard.module.css';

// ── Props ────────────────────────────────────────────────────────

export interface HSECardProps {
  /** Which HSE card this is. Determines icon and title. */
  category: HseCategory;
  /** Engine score (must include `hse` extension from incident_count type). */
  score: SubKpiScore;
  /** Whether the drill-down panel for this card is currently open. */
  expanded?: boolean;
  /** Called when the user clicks the card body to toggle expansion. */
  onExpand?: () => void;
  /** Called when the user clicks the "+ Report" button. */
  onReportClick: () => void;
  /** Optional override for the title (defaults to category-derived label). */
  displayName?: string;
  /**
   * When true, render a pencil-to-configure icon in the header. Only
   * passed by parents that have admin permission on the project. Mirrors
   * the DEV-816 pattern used on MissionSubKpiCard.
   */
  canConfigure?: boolean;
  /**
   * Called when the user clicks the pencil. Parent owns the tray state
   * and renders the wizard — HSECard stays presentational.
   */
  onConfigClick?: () => void;
  /** Optional debug overlay. */
  debugMode?: boolean;
}

// ── Color derivation ────────────────────────────────────────────

type Color = 'green' | 'yellow' | 'red';

function colorForScore(score: SubKpiScore): Color {
  if (score.score_0_100 === null) return 'green';
  if (score.score_0_100 >= 100) return 'green';
  if (score.score_0_100 >= 70) return 'yellow';
  return 'red';
}

function severityColor(severity: HseSeverity): Color {
  return severity === 'near-miss' || severity === 'first-aid'
    ? 'yellow'
    : 'red';
}

// ── Number / date formatting helpers ────────────────────────────

/** "today" / "yesterday" / "N days ago" / absolute date for older. */
function formatRelativeDate(
  iso: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!iso) return t('hse.dateUnknown');
  const then = Date.parse(iso + (iso.length === 10 ? 'T00:00:00Z' : ''));
  if (Number.isNaN(then)) return iso;
  const now = Date.now();
  const days = Math.floor((now - then) / 86400000);
  if (days <= 0) return t('hse.relativeDate.today');
  if (days === 1) return t('hse.relativeDate.yesterday');
  if (days < 30) return t('hse.relativeDate.daysAgo', { count: days });
  return new Date(then).toLocaleDateString();
}

// ── Icons (heart / shield / leaf — minimal inline SVG) ──────────

const ICONS: Record<HseCategory, React.ReactNode> = {
  health: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  safety: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  environment: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6" />
    </svg>
  ),
};

// Bolt icon for the AUTO badge.
const BoltIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
    style={{ width: 9, height: 9 }}>
    <polygon points="13,2 4,14 11,14 9,22 20,10 13,10" />
  </svg>
);

// + icon for the Report button.
const PlusIcon = (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
    stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// Pencil icon for the configure button (DEV-822 — mirrors MissionSubKpiCard pattern).
const PencilIcon = (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

// ── Component ───────────────────────────────────────────────────

export function HSECard({
  category,
  score,
  expanded = false,
  onExpand,
  onReportClick,
  displayName,
  canConfigure = false,
  onConfigClick,
  debugMode = false,
}: HSECardProps) {
  const { t } = useAppTranslation('kpi');

  const hse: HseScoreExtension | undefined = score.hse;
  const color = colorForScore(score);
  const periodCount = hse?.period_count ?? 0;
  const hasIncidents = periodCount > 0;
  const isFresh = hse?.fresh_incident === true && hasIncidents;

  // Title — defaults to the category label.
  const title = displayName ?? t(`hse.category.${category}`);

  // Big number + unit.
  const effectiveMode = hse?.effective_display_mode ?? 'streak';
  const bigValue = effectiveMode === 'streak' ? hse?.streak_days ?? 0 : periodCount;
  const bigUnit = useMemo(() => {
    if (effectiveMode === 'streak') {
      return t('hse.unit.daysIncidentFree', { count: bigValue });
    }
    return t('hse.unit.incidents', { count: bigValue });
  }, [effectiveMode, bigValue, t]);

  // Foot preview — varies by state.
  const footContent = useMemo(() => {
    if (!hasIncidents) {
      // State 1: clean
      const lastDate = hse?.last_incident_date ?? null;
      if (!lastDate) {
        return (
          <span className={styles.previewText}>
            {t('hse.foot.lastIncidentNever')}
          </span>
        );
      }
      return (
        <span className={styles.previewText}>
          {t('hse.foot.lastIncidentLabel')}{' '}
          <strong>{formatRelativeDate(lastDate, t)}</strong>
        </span>
      );
    }

    // States 2/3/4: incidents present
    const sev = hse?.most_recent_severity;
    const recentTitle = hse?.most_recent_title;
    const recentDateRel = formatRelativeDate(hse?.last_incident_date ?? null, t);

    if (periodCount > 1) {
      // State 4: multi-incident
      return (
        <span className={styles.previewText}>
          {sev && (
            <span
              className={styles.severityMarker}
              data-severity={sev}
            >
              {t(`hse.severity.${sev}`)}
            </span>
          )}
          {t('hse.foot.mostRecent')} <strong>{recentDateRel}</strong>
        </span>
      );
    }

    // States 2/3: single incident
    return (
      <span className={styles.previewText}>
        {sev && (
          <span
            className={styles.severityMarker}
            data-severity={sev}
          >
            {t(`hse.severity.${sev}`)}
          </span>
        )}
        {recentTitle ?? t('hse.foot.unknownIncident')}{' '}
        &middot; <strong>{recentDateRel}</strong>
      </span>
    );
  }, [hasIncidents, hse, periodCount, t]);

  // Mode badge — only shown when the engine has overridden the configured
  // display mode (auto-promote: configured=streak but a current-period
  // incident pushed effective=count). In steady state the unit text below
  // the big number already tells the user what mode they're in
  // ("days incident-free" vs "incident") — so the badge is informational
  // noise except in the auto-promote case.
  const modeBadge = useMemo(() => {
    if (!hse || !hse.auto_promoted) return null;
    return (
      <span
        className={`${styles.modeBadge} ${styles.modeBadgeAuto}`}
        title={t('hse.badge.autoPromotedTooltip')}
      >
        {BoltIcon}
        <span>{t('hse.badge.autoCount')}</span>
      </span>
    );
  }, [hse, t]);

  // Meter fill — visual cue, not used for color but for "comfort margin".
  const meterPct = useMemo(() => {
    if (!hasIncidents) {
      // Streak comfort: 365 days = 100% comfort
      const days = hse?.streak_days ?? 0;
      return Math.min(100, (days / 365) * 100);
    }
    // Incident counts: each incident eats 50% of the meter
    return Math.max(0, 100 - periodCount * 50);
  }, [hasIncidents, hse, periodCount]);

  const handleCardClick = () => {
    if (!hasIncidents) return; // No drill-down when clean
    onExpand?.();
  };

  const handleReportClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    onReportClick();
  };

  const cardClasses = [
    styles.card,
    isFresh ? styles.fresh : '',
    !hasIncidents ? styles.noIncidents : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cardClasses}
      data-color={color}
      data-expanded={expanded}
      onClick={handleCardClick}
      role={hasIncidents ? 'button' : undefined}
      tabIndex={hasIncidents ? 0 : undefined}
      onKeyDown={
        hasIncidents
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onExpand?.();
              }
            }
          : undefined
      }
      aria-label={t('hse.card.ariaLabel', {
        category: title,
        count: periodCount,
      })}
    >
      <div className={styles.head}>
        <div className={styles.icon} aria-hidden="true">
          {ICONS[category]}
        </div>
        <div className={styles.title}>
          {title}
          {isFresh && (
            <span className={styles.newBadge} data-color={color}>
              &bull; {t('hse.badge.new')}
            </span>
          )}
        </div>
        {modeBadge}
        {/* DEV-822: pencil-to-configure. Only shows when parent grants
            canConfigure (admin-level permission). Stops click propagation
            so opening the tray doesn't also toggle expanded state. */}
        {canConfigure && (
          <button
            className={styles.reportBtn}
            onClick={(e) => {
              e.stopPropagation();
              onConfigClick?.();
            }}
            type="button"
            aria-label={t('hse.button.configure', 'Configure HSE card')}
            title={t('hse.button.configure', 'Configure HSE card')}
          >
            {PencilIcon}
          </button>
        )}
        <button
          className={styles.reportBtn}
          onClick={handleReportClick}
          type="button"
          aria-label={t('hse.button.reportIncident')}
          title={t('hse.button.reportIncident')}
        >
          {PlusIcon}
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.bignum}>{bigValue}</div>
        <div className={styles.bigunit}>{bigUnit}</div>
      </div>

      <div className={styles.foot}>
        {footContent}
        {hasIncidents && (
          <span className={styles.expandAffordance}>
            {expanded
              ? t('hse.affordance.hide')
              : periodCount > 1
              ? t('hse.affordance.viewAll', { count: periodCount })
              : t('hse.affordance.view')}
          </span>
        )}
      </div>

      <div className={styles.meter} aria-hidden="true">
        <div
          className={styles.meterFill}
          style={{ width: `${meterPct}%` }}
        />
      </div>

      {debugMode && hse && (
        <div className={styles.debugOverlay}>
          <div>configured: {hse.configured_display_mode}</div>
          <div>effective: {hse.effective_display_mode}</div>
          <div>auto: {String(hse.auto_promoted)}</div>
          <div>fresh: {String(hse.fresh_incident)}</div>
          <div>streak: {hse.streak_days}d</div>
          <div>count: {hse.period_count}</div>
        </div>
      )}
    </div>
  );
}

export default HSECard;
